import React from 'react';
import { DollarSignIcon, PercentIcon, TrendingUpIcon, WalletIcon, UsersIcon, CreditCardIcon, BarChart3Icon, DownloadIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import KPICard from '../components/Dashboard/KPICard';
import InsightCard from '../components/Dashboard/InsightCard';
import { kpiData, revenueExpensesData, insightsData } from '../utils/mockData';
const Dashboard = () => {
  // Map KPI icons based on the KPI title
  const getKPIIcon = (title: string) => {
    switch (title) {
      case 'Ventas del mes':
        return <DollarSignIcon size={24} data-id="element-928" />;
      case 'Utilidad Bruta':
      case 'Utilidad Neta':
        return <TrendingUpIcon size={24} data-id="element-929" />;
      case 'Cash disponible':
        return <WalletIcon size={24} data-id="element-930" />;
      case 'Cuentas por cobrar':
        return <UsersIcon size={24} data-id="element-931" />;
      case 'Cuentas por pagar':
        return <CreditCardIcon size={24} data-id="element-932" />;
      case 'Margen bruto':
      case 'Margen neto':
        return <PercentIcon size={24} data-id="element-933" />;
      default:
        return <BarChart3Icon size={24} data-id="element-934" />;
    }
  };
  // Format chart values to show in thousands (K)
  const formatChartValue = (value: number) => {
    return `$${(value / 1000).toFixed(0)}K`;
  };
  return <div data-id="element-935">
      <div className="mb-6 flex justify-between items-start" data-id="element-936">
        <div data-id="element-937">
          <h1 className="text-2xl font-bold text-gray-800" data-id="element-938">
            Dashboard Financiero
          </h1>
          <p className="text-gray-600" data-id="element-939">
            Vista general de los indicadores financieros clave de tu negocio
          </p>
        </div>
        <button className="flex items-center space-x-2 text-sm bg-blue-600 text-white px-4 py-2.5 rounded-md hover:bg-blue-700 transition-colors" data-id="element-940">
          <DownloadIcon size={18} data-id="element-941" />
          <span data-id="element-942">Exportar PDF</span>
        </button>
      </div>
      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-943">
        {kpiData.map(kpi => <KPICard key={kpi.id} title={kpi.title} value={kpi.value} change={kpi.change} icon={getKPIIcon(kpi.title)} prefix={kpi.prefix} suffix={kpi.suffix} data-id="element-944" />)}
      </div>
      {/* Main Chart and Insights Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8" data-id="element-945">
        <div className="lg:col-span-2 bg-white p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-946">
          <h2 className="text-lg font-semibold mb-4" data-id="element-947">Ingresos vs Gastos</h2>
          <div className="h-80" data-id="element-948">
            <ResponsiveContainer width="100%" height="100%" data-id="element-949">
              <BarChart data={revenueExpensesData} margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 0
            }} data-id="element-950">
                <CartesianGrid strokeDasharray="3 3" vertical={false} data-id="element-951" />
                <XAxis dataKey="month" data-id="element-952" />
                <YAxis tickFormatter={formatChartValue} data-id="element-953" />
                <Tooltip formatter={value => [`$${value.toLocaleString()}`, undefined]} labelFormatter={label => `Mes: ${label}`} data-id="element-954" />
                <Legend data-id="element-955" />
                <Bar dataKey="ingresos" name="Ingresos" fill="#4F46E5" radius={[4, 4, 0, 0]} data-id="element-956" />
                <Bar dataKey="gastos" name="Gastos" fill="#F97316" radius={[4, 4, 0, 0]} data-id="element-957" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="space-y-4" data-id="element-958">
          <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-959">
            <h2 className="text-lg font-semibold mb-3" data-id="element-960">Insights automáticos</h2>
            <div className="space-y-3" data-id="element-961">
              {insightsData.slice(0, 3).map(insight => <InsightCard key={insight.id} type={insight.type as 'positive' | 'negative' | 'warning' | 'info'} message={insight.message} data-id="element-962" />)}
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-963">
            <h2 className="text-lg font-semibold mb-2" data-id="element-964">
              Acciones recomendadas
            </h2>
            <ul className="space-y-2" data-id="element-965">
              <li className="flex items-start" data-id="element-966">
                <div className="flex-shrink-0 mt-0.5 text-blue-600" data-id="element-967">
                  <span className="inline-block w-2 h-2 rounded-full bg-blue-600 mr-2" data-id="element-968"></span>
                </div>
                <p className="text-sm text-gray-600" data-id="element-969">
                  Revisar estrategia de gestión de inventario
                </p>
              </li>
              <li className="flex items-start" data-id="element-970">
                <div className="flex-shrink-0 mt-0.5 text-blue-600" data-id="element-971">
                  <span className="inline-block w-2 h-2 rounded-full bg-blue-600 mr-2" data-id="element-972"></span>
                </div>
                <p className="text-sm text-gray-600" data-id="element-973">
                  Agilizar proceso de cobranza de clientes
                </p>
              </li>
              <li className="flex items-start" data-id="element-974">
                <div className="flex-shrink-0 mt-0.5 text-blue-600" data-id="element-975">
                  <span className="inline-block w-2 h-2 rounded-full bg-blue-600 mr-2" data-id="element-976"></span>
                </div>
                <p className="text-sm text-gray-600" data-id="element-977">
                  Evaluar reducción de gastos operativos
                </p>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>;
};
export default Dashboard;